/**
 * chatSeen — when a game's chat counts as read.
 *
 * A message is read once its line has actually been on screen in the game log
 * while the viewer is live (game screen focused, app in the foreground). A
 * line that arrives and is pushed out of view (more log rows, or the viewer is
 * scrolled elsewhere) before it is seen stays unread, so the map bar's chat
 * button still badges it and opens straight to the chat-only log.
 *
 * The read marker (useChatUnread) is the newest seen message id; it only ever
 * moves forward. RN-free for unit testing.
 */
import type { ChatMessageResponse } from '@warcarp/api-types'

/**
 * The marker after seeing `visibleIds`: the newest visible message, when it is
 * newer than the current marker. `null` means no change. `seenId` null means
 * nothing read yet; an id missing from `messages` (trimmed out of the loaded
 * window) counts as older than everything loaded, as in unreadCount.
 */
export function advanceSeenMarker(
  messages: readonly Pick<ChatMessageResponse, 'id'>[],
  seenId: string | null,
  visibleIds: Iterable<string>,
): string | null {
  const index = new Map(messages.map((message, i) => [message.id, i]))
  const current = seenId === null ? -1 : index.get(seenId) ?? -1
  let newest = -1
  for (const id of visibleIds) newest = Math.max(newest, index.get(id) ?? -1)
  return newest > current ? messages[newest]!.id : null
}

/** The chat message ids among a FlatList's viewable log rows. */
export function visibleChatMessageIds(
  viewableItems: readonly { isViewable: boolean; item: unknown }[],
): string[] {
  const ids: string[] = []
  for (const { isViewable, item } of viewableItems) {
    const messageId = isViewable ? (item as { chat?: { messageId?: string } } | null)?.chat?.messageId : undefined
    if (messageId) ids.push(messageId)
  }
  return ids
}
